/**
 * Shared font-family normalization for the TipTap editor.
 *
 * Two ingestion paths feed font-family values into the editor:
 *
 *   1. DOCX import (`utils/docxImport.ts`) — reads `w:rFonts/@w:ascii` from
 *      Word's XML, which is a single bare font name like "Calibri" or
 *      "Times New Roman".
 *   2. Clipboard paste (`TipTapEditor.tsx` `transformPastedHTML`) — reads
 *      `font-family` from inline CSS on pasted spans, which can be a stack
 *      like `"Aptos", sans-serif` (with quotes, fallbacks, etc.).
 *
 * Both paths converge here so the editor's `textStyle` mark only ever
 * carries one of the canonical preset values exposed in the toolbar
 * dropdown. Anything else (e.g. Word's current default "Aptos") is
 * mapped to a known preset when possible, or dropped so the editor
 * falls back to its CSS default (Times New Roman).
 *
 * Keeping this mapping in one place prevents the divergence that caused
 * the "pasted text renders in a different font but the dropdown shows
 * Times New Roman" bug: prior to this util, only docxImport.ts
 * normalized fonts, while the paste path silently accepted whatever
 * font Word put on the clipboard.
 */

/**
 * The toolbar's font dropdown presets. The empty value is the editor's
 * CSS default (Times New Roman) — picking it unsets the fontFamily mark.
 * SINGLE SOURCE OF TRUTH: EditorToolbar imports this list.
 */
export const FONT_FAMILIES = [
  { label: "Times New Roman", value: "" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Courier New", value: "Courier New, monospace" },
  { label: "Trebuchet MS", value: "Trebuchet MS, sans-serif" },
  { label: "Comic Sans MS", value: "Comic Sans MS, cursive" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Palatino Linotype", value: "Palatino Linotype, Palatino, serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Calibri", value: "Calibri, Candara, sans-serif" },
  { label: "Cambria", value: "Cambria, Georgia, serif" },
  { label: "Garamond", value: "Garamond, Baskerville, serif" },
  { label: "Consolas", value: "Consolas, Monaco, monospace" },
  { label: "Lucida Sans", value: "Lucida Sans, Lucida Grande, sans-serif" },
  { label: "Book Antiqua", value: "Book Antiqua, Palatino, serif" },
  { label: "Century Gothic", value: "Century Gothic, sans-serif" },
  { label: "Impact", value: "Impact, Charcoal, sans-serif" },
  // CJK families — this is a bilingual EN/中文 platform and Chinese documents
  // are common. Values fall back across Windows (SimSun/SimHei…) and macOS
  // (Songti/Heiti/PingFang) system fonts. Labels show the native name so the
  // dropdown renders it in the right glyphs.
  { label: "宋体 (SimSun)", value: '"SimSun", "宋体", "Songti SC", serif' },
  { label: "黑体 (SimHei)", value: '"SimHei", "黑体", "Heiti SC", sans-serif' },
  { label: "楷体 (KaiTi)", value: '"KaiTi", "楷体", "Kaiti SC", STKaiti, serif' },
  {
    label: "仿宋 (FangSong)",
    value: '"FangSong", "仿宋", STFangsong, serif',
  },
  {
    label: "微软雅黑 (Microsoft YaHei)",
    value: '"Microsoft YaHei", "微软雅黑", "PingFang SC", "Heiti SC", sans-serif',
  },
  {
    label: "等线 (DengXian)",
    value: '"DengXian", "等线", "PingFang SC", sans-serif',
  },
];

/**
 * Lowercase font name (Word-style or CSS primary-name) → canonical
 * CSS font-family value matching the FONT_FAMILIES list above.
 */
export const SUPPORTED_FONT_MAP: Record<string, string> = {
  arial: "Arial, sans-serif",
  "times new roman": "Times New Roman, serif",
  georgia: "Georgia, serif",
  verdana: "Verdana, sans-serif",
  "courier new": "Courier New, monospace",
  "trebuchet ms": "Trebuchet MS, sans-serif",
  "comic sans ms": "Comic Sans MS, cursive",
  helvetica: "Helvetica, Arial, sans-serif",
  "palatino linotype": "Palatino Linotype, Palatino, serif",
  palatino: "Palatino Linotype, Palatino, serif",
  tahoma: "Tahoma, Geneva, sans-serif",
  calibri: "Calibri, Candara, sans-serif",
  candara: "Calibri, Candara, sans-serif",
  cambria: "Cambria, Georgia, serif",
  garamond: "Garamond, Baskerville, serif",
  consolas: "Consolas, Monaco, monospace",
  "lucida sans": "Lucida Sans, Lucida Grande, sans-serif",
  "lucida grande": "Lucida Sans, Lucida Grande, sans-serif",
  "book antiqua": "Book Antiqua, Palatino, serif",
  "century gothic": "Century Gothic, sans-serif",
  impact: "Impact, Charcoal, sans-serif",
  // CJK — keyed by both the Word/Latin name and the native name so imported
  // eastAsia fonts (e.g. w:eastAsia="宋体" or "SimSun") canonicalize to a preset.
  simsun: '"SimSun", "宋体", "Songti SC", serif',
  宋体: '"SimSun", "宋体", "Songti SC", serif',
  songti: '"SimSun", "宋体", "Songti SC", serif',
  "songti sc": '"SimSun", "宋体", "Songti SC", serif',
  simhei: '"SimHei", "黑体", "Heiti SC", sans-serif',
  黑体: '"SimHei", "黑体", "Heiti SC", sans-serif',
  "heiti sc": '"SimHei", "黑体", "Heiti SC", sans-serif',
  kaiti: '"KaiTi", "楷体", "Kaiti SC", STKaiti, serif',
  楷体: '"KaiTi", "楷体", "Kaiti SC", STKaiti, serif',
  "kaiti sc": '"KaiTi", "楷体", "Kaiti SC", STKaiti, serif',
  fangsong: '"FangSong", "仿宋", STFangsong, serif',
  仿宋: '"FangSong", "仿宋", STFangsong, serif',
  "microsoft yahei": '"Microsoft YaHei", "微软雅黑", "PingFang SC", "Heiti SC", sans-serif',
  微软雅黑: '"Microsoft YaHei", "微软雅黑", "PingFang SC", "Heiti SC", sans-serif',
  "pingfang sc": '"Microsoft YaHei", "微软雅黑", "PingFang SC", "Heiti SC", sans-serif',
  dengxian: '"DengXian", "等线", "PingFang SC", sans-serif',
  等线: '"DengXian", "等线", "PingFang SC", sans-serif',
};

/** The editor's fallback when no font-family mark is set. */
export const DEFAULT_FONT = "Times New Roman, serif";

/**
 * Look up a font name (Word-style bare name OR CSS font-family stack)
 * in `SUPPORTED_FONT_MAP` and return the canonical preset value.
 *
 *   - `"Arial"` → `"Arial, sans-serif"`
 *   - `'"Aptos", sans-serif'` → null (no entry)
 *   - `"Calibri Light"` → `"Calibri, Candara, sans-serif"` (first-word match)
 *   - `""` / `undefined` → null
 *
 * Returns null when there's no match, so callers can decide what to do:
 *
 *   - DOCX import: fall back to `DEFAULT_FONT` (always set a font).
 *   - Paste: strip the `font-family` style entirely so the editor's CSS
 *     default takes over and the dropdown reflects truth.
 */
export function canonicalizeFontFamily(
  raw: string | undefined | null
): string | null {
  if (!raw) return null;

  // Take the primary entry from a stack ("Aptos", sans-serif → Aptos),
  // strip surrounding quotes, normalize case.
  const primary = raw
    .split(",")[0]
    .trim()
    .replace(/^["']|["']$/g, "")
    .trim()
    .toLowerCase();
  if (!primary) return null;

  // Exact match (handles "times new roman", "lucida sans", etc.)
  if (SUPPORTED_FONT_MAP[primary]) return SUPPORTED_FONT_MAP[primary];

  // First-word match (handles "Calibri Light" → "calibri")
  const firstWord = primary.split(/\s+/)[0];
  if (SUPPORTED_FONT_MAP[firstWord]) return SUPPORTED_FONT_MAP[firstWord];

  return null;
}

/**
 * DOCX-import flavor: always returns a usable font value. Unsupported
 * inputs fall back to `DEFAULT_FONT` rather than null. Keep this
 * separate from the null-returning core so paste and import can have
 * different "what to do when unknown" behaviors.
 */
export function resolveFontFamilyOrDefault(raw: string | undefined): string {
  return canonicalizeFontFamily(raw) ?? DEFAULT_FONT;
}

/**
 * Resolve a textStyle.fontFamily value to the toolbar dropdown's option
 * value. A controlled <select> whose value matches NO option silently
 * displays the FIRST option ("Times New Roman") — which both lies about
 * the current font and makes "re-selecting" Times New Roman a no-op (no
 * change event). So:
 *
 *   - known fonts (any quoting/stack/Word-variant) → the preset value
 *   - the editor default (Times New Roman) → "" (the default entry)
 *   - unknown fonts → returned RAW; the caller must render a dynamic
 *     option for them (see isPresetFontValue/primaryFontName)
 */
export function resolveDropdownFont(raw: string | undefined | null): string {
  if (!raw) return "";
  if (FONT_FAMILIES.some(f => f.value === raw)) return raw;
  const canonical = canonicalizeFontFamily(raw);
  if (canonical === DEFAULT_FONT) return "";
  if (canonical && FONT_FAMILIES.some(f => f.value === canonical)) {
    return canonical;
  }
  return raw;
}

/** Is this value one of the dropdown presets (including the "" default)? */
export function isPresetFontValue(value: string): boolean {
  return FONT_FAMILIES.some(f => f.value === value);
}

/** Human label for a non-preset font value ("\"Aptos\", sans-serif" → "Aptos"). */
export function primaryFontName(value: string): string {
  return value
    .split(",")[0]
    .trim()
    .replace(/^["']|["']$/g, "")
    .trim();
}
